import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from './../data/auth.service';
import { Component, OnInit, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { ButtonComponent } from 'src/app/shared/ui-common/button/button.component';
import { AuthLayoutComponent } from '../ui/auth-layout/auth-layout.component';
import { PinCodeComponent } from '../ui/pin-code/pin-code.component';
import { combineLatest, lastValueFrom, map } from 'rxjs';
import { AppLoginOutcome } from '../data/enums/app-login-outcome.enum';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { PillService } from 'src/app/shared/data/pill.service';
import { SharedService } from 'src/app/shared/data/shared.service';
import { TranslatePipe } from 'src/app/shared/data/pipes/translate.pipe';
import { TranslationService } from 'src/app/shared/data/translation.service';
import { ValidateCredentials } from '../data/models/validate-credentials.model';
import { EnvironmentService } from 'src/app/shared/data/environment.service';

enum Step {
  Email = 'email',
  Pin = 'pin',
  SsoLinking = 'sso-linking',
  ForgotCode = 'forgot-code',
}

@Component({
  selector: 'app-feature-login',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AuthLayoutComponent,
    ButtonComponent,
    PinCodeComponent,
    TranslatePipe
  ],
  templateUrl: './feature-login.component.html',
  styleUrls: ['./feature-login.component.scss', '../ui/auth.styles.scss'],
})
export class FeatureLoginComponent implements OnInit {
  @ViewChild('authLayout') authLayout!: AuthLayoutComponent;

  loginForm: FormGroup;
  forgotCodeForm: FormGroup;

  steps = Step;
  showLongPassword = false;
  isTransitioning = false;
  step = signal<Step>(Step.Email);
  userNameSignal = signal<string>('');
  emailValidationErrorSignal = signal<string | null>(null);
  pinValidationErrorSignal = signal<string | null>(null);
  forgotCodeEmailRequested = signal<boolean>(false);
  forgotCodeEmailValidationErrorSignal = signal<string | null>(null);
  companyLogo = signal<string>('');
  companyName = signal<string>('');
  languageId = signal<number>(1045);

  get emailControl(): AbstractControl<string> | null {
    return this.loginForm.get('email');
  }

  get pinControl(): AbstractControl<string> | null {
    return this.loginForm.get('pin');
  }

  get rememberMeControl(): AbstractControl<boolean> | null {
    return this.loginForm.get('rememberMe');
  }

  get userName(): string {
    return this.userNameSignal();
  }

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private pillService: PillService,
    private sharedService: SharedService,
    private translationService: TranslationService,
    private route: ActivatedRoute,
    private environmentService: EnvironmentService
  ) {
    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      pin: ['', [Validators.required, Validators.pattern(/^\d{4}$/)]],
      password: ['', [Validators.required]],
      rememberMe: [false],
    });

    this.forgotCodeForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  ngOnInit(): void {
    combineLatest([this.route.params, this.route.queryParams])
      .pipe(
        map(async (params) => {
          const token = params[0]['token'];
          const encodedJwt = params[1]['encodedJwt'] || params[1]['encodedjwt'];

          if (token && !encodedJwt) {
            await this.reminderTokenBasedAccess(token);

            return;
          }

          if (encodedJwt) {
            this.tryToLoginWithEncodedJwt(encodedJwt);
          }
        })
      )
      .subscribe();
  }

  async reminderTokenBasedAccess(token: string): Promise<void> {
    try {
      const emailValidation = await lastValueFrom(this.authService.onLoginWithToken(token));
      const email = emailValidation.email;

      this.loginForm.patchValue({ email });
      this.setSuccessEmailConfiguration(emailValidation);

      if (!emailValidation.isSsoAccount) {
        this.goToNextStep(Step.Pin);
      } else {
        this.startSsoLogin(emailValidation);
      }
    } catch (error) {
      this.router.navigate(['/login']);
    }
  }

  async submitEmail(): Promise<void> {
    const emailFormField = this.loginForm.get('email');

    if (!emailFormField?.valid) {
      return;
    }

    const email = emailFormField.value;
    const emailValidation = await lastValueFrom(this.authService.onValidateEmail(email));

    switch (emailValidation.outcome) {
      case AppLoginOutcome.Success:
        this.setSuccessEmailConfiguration(emailValidation);

        if (!emailValidation.isSsoAccount) {
          this.goToNextStep(Step.Pin);
        } else {
          this.startSsoLogin(emailValidation);
        }

        break;
      case AppLoginOutcome.EmailMismatch:
      case AppLoginOutcome.IncorrectEmail:
      case AppLoginOutcome.UnknownUsername:
        this.emailValidationErrorSignal.set('WebApp.v1.Auth.Login.EmailValidationError.UserNotFound');
        break;
      case AppLoginOutcome.UserLockedOut:
      case AppLoginOutcome.UserDeleted:
        this.emailValidationErrorSignal.set('WebApp.v1.Auth.Login.EmailValidationError.UserLockedOut');
        break;
      case AppLoginOutcome.UserInactive:
        this.emailValidationErrorSignal.set('WebApp.v1.Auth.Login.EmailValidationError.UserInactive');
        break;
      default:
        this.emailValidationErrorSignal.set('WebApp.v1.Auth.Login.EmailValidationError.UnknownError');
        break;
    }
  }

  async tryToLoginWithEncodedJwt(encodedJwt: string): Promise<void> {
    const email = localStorage.getItem('sso-email');

    if (encodedJwt && email) {
      try {
        localStorage.removeItem('sso-email');
        const token = await lastValueFrom(this.authService.onLoginSso(encodedJwt, email));
        this.authService.setCookie(token, true);
        this.pillService.clearMenuItems();

        await this.translationService.refreshTranslationsFromApi(this.languageId().toString());
        this.router.navigate(['/available-surveys']);
      } catch (error) {
        console.error('error', error);
      }
    } else {
      this.router.navigate(['/login']);
    }
  }

  startSsoLogin(emailValidation: ValidateCredentials): void {
    if (emailValidation.isSsoFirstLogin) {
      this.goToNextStep(Step.SsoLinking);
      this.companyName.set(emailValidation.companyName);
    } else {
      this.continueToSsoPage();
    }
  }

  continueToSsoPage(): void {
    const ssoUrl = this.environmentService.getEnvironment().ssoUrl;
    const redirectUrl = window.location.href;

    localStorage.setItem('sso-email', this.emailControl?.value ?? '');
    window.location.href = `${ssoUrl}?redirect=${redirectUrl}`;
  }

  async submitPin(pin: string): Promise<void> {
    this.pinValidationErrorSignal.set(null);

    if (pin.length !== 4) {
      return;
    }

    this.loginForm.patchValue({ pin });
    const pinFormField = this.loginForm.get('pin');

    if (!pinFormField?.valid) {
      return;
    }

    const password = pinFormField.value;
    const email = this.loginForm.get('email')?.value;
    const rememberMe = this.loginForm.get('rememberMe')?.value;

    try {
      const jwtToken = await lastValueFrom(this.authService.onLogin(email, password));
      this.authService.setCookie(jwtToken, rememberMe);
      await this.translationService.refreshTranslationsFromApi(this.languageId().toString());
      this.pillService.clearMenuItems();

      this.router.navigate(['/available-surveys']);
    } catch (error) {
      if ((error as HttpErrorResponse).status === HttpStatusCode.Unauthorized) {
        this.pinValidationErrorSignal.set('WebApp.v1.Auth.Login.PinValidationError.IncorrectPin');
      }
    }
  }

  async submitPassword(): Promise<void> {
    const password = this.loginForm.get('password')?.value;
    const email = this.loginForm.get('email')?.value;
    const rememberMe = this.loginForm.get('rememberMe')?.value;

    try {
      const jwtToken = await lastValueFrom(this.authService.onLogin(email, password));
      this.authService.setCookie(jwtToken, rememberMe);
      await this.translationService.refreshTranslationsFromApi(this.languageId().toString());
      this.pillService.clearMenuItems();
      this.router.navigate(['/available-surveys']);
    } catch (error) {
      if ((error as HttpErrorResponse).status === HttpStatusCode.Unauthorized) {
        this.pinValidationErrorSignal.set('WebApp.v1.Auth.Login.PinValidationError.IncorrectPassword');
      }
    }
  }

  goToEmail(): void {
    this.goToNextStep(Step.Email);
    this.loginForm.get('pin')?.setValue('');
    this.loginForm.get('email')?.setValue('');
    this.loginForm.get('email')?.markAsUntouched();
  }

  goToNextStep(step: Step): void {
    this.isTransitioning = true;

    setTimeout(() => {
      this.step.set(step);
      this.isTransitioning = false;
    }, 400);
  }

  toggleRememberMe(): void {
    this.loginForm.get('rememberMe')?.setValue(!this.loginForm.get('rememberMe')?.value);
  }

  submitForgotCode(): void {
    if (this.forgotCodeForm.valid) {
      const email = this.forgotCodeForm.get('email')?.value;

      this.authService.onRequestResetPassword(email).subscribe({
        next: () => {
          this.forgotCodeEmailValidationErrorSignal.set('');
          this.forgotCodeEmailRequested.set(true);
        },
        error: (errorResponse: HttpErrorResponse) => {
          this.forgotCodeEmailValidationErrorSignal.set(errorResponse.error);
        }
      });
    }
  }

  scrollToWhy(): void {
    const whyElement = document.getElementById('image-container');

    if (whyElement) {
      whyElement.scrollIntoView({ behavior: 'smooth' });
    }
  }

  showHowdyVideo(): void {
    this.authLayout.showHowdyVideo = true;
  }

  setSuccessEmailConfiguration(emailValidation: ValidateCredentials): void {
    this.languageId.set(emailValidation.languageId);
    this.translationService.setAuthLanguage(emailValidation.languageId);
    this.translationService.loadTranslationsFromJson();
    this.sharedService.setCompanyId(emailValidation.companyId);
    this.companyLogo.set(this.sharedService.getCompanyLogoUrl());
    this.userNameSignal.set(emailValidation.howdyUsername);
    this.showLongPassword = emailValidation.isLongPassword;
  }
}
